import { supabase } from '@/lib/supabase'

/**
 * Check which clients have profile images available
 */
export const getClientsWithImages = async (clientIds: number[]): Promise<number[]> => {
  try {
    const clientsWithImages: number[] = []
    
    // Process in batches to avoid overwhelming the API
    const batchSize = 10
    for (let i = 0; i < clientIds.length; i += batchSize) {
      const batch = clientIds.slice(i, i + batchSize)
      
      // Check if any of the common extensions exist for each client
      await Promise.all(
        batch.map(async (clientId) => {
          const extensions = ['jpg', 'jpeg', 'png', 'webp']
          
          for (const ext of extensions) {
            const filePath = `${clientId}.${ext}`
            
            try {
              // Try to get metadata to see if file exists
              const { data: metadata, error } = await supabase.storage
                .from('client-images')
                .list('', { 
                  limit: 1,
                  search: filePath 
                })
              
              if (!error && metadata && metadata.length > 0) {
                clientsWithImages.push(clientId)
                break // Found an image for this client, no need to check other extensions
              }
            } catch {
              // Continue to next extension
              continue
            }
          }
        })
      )
      
      // Small delay between batches
      if (i + batchSize < clientIds.length) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }
    
    return clientsWithImages
  } catch (error) {
    console.error('Error checking which clients have images:', error)
    return []
  }
}

/**
 * Get client image URL with proper error handling and fallbacks
 */
export const getClientImageUrl = async (clientId: number): Promise<string | null> => {
  try {
    // Try multiple file extensions
    const extensions = ['jpg', 'jpeg', 'png', 'webp']
    
    for (const ext of extensions) {
      const filePath = `${clientId}.${ext}`
      
      try {
        // First try to get a signed URL
        const { data: signedData, error: signedError } = await supabase.storage
          .from('client-images')
          .createSignedUrl(filePath, 60 * 60 * 24) // 24 hours expiry
        
        if (signedData?.signedUrl) {
          return signedData.signedUrl
        }
        
        // If signed URL fails, try public URL
        const { data: publicData } = supabase.storage
          .from('client-images')
          .getPublicUrl(filePath)
        
        if (publicData?.publicUrl) {
          // Test if the URL is accessible
          try {
            const response = await fetch(publicData.publicUrl, { method: 'HEAD' })
            if (response.ok) {
              return publicData.publicUrl
            }
          } catch (fetchError) {
            // Silently continue to next extension - don't log warnings for missing files
            continue
          }
        }
      } catch (storageError) {
        // Silently continue to next extension - don't log errors for missing files
        continue
      }
    }
    
    // If no image found, return null (no console warning for missing images)
    return null
    
  } catch (error) {
    // Only log actual errors, not missing files
    console.error(`Unexpected error getting image for client ${clientId}:`, error)
    return null
  }
}

/**
 * Get multiple client image URLs efficiently
 */
export const getClientImageUrls = async (clientIds: number[]): Promise<{ [clientId: number]: string | null }> => {
  const urls: { [clientId: number]: string | null } = {}
  
  // Process in batches to avoid overwhelming the API
  const batchSize = 5
  for (let i = 0; i < clientIds.length; i += batchSize) {
    const batch = clientIds.slice(i, i + batchSize)
    
    await Promise.all(
      batch.map(async (clientId) => {
        urls[clientId] = await getClientImageUrl(clientId)
      })
    )
    
    // Small delay between batches
    if (i + batchSize < clientIds.length) {
      await new Promise(resolve => setTimeout(resolve, 100))
    }
  }
  
  return urls
}

/**
 * Check if an image URL is valid and accessible
 */
export const validateImageUrl = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { method: 'HEAD' })
    return response.ok
  } catch {
    return false
  }
}

/**
 * Get a fallback avatar URL for clients without images
 */
export const getFallbackAvatarUrl = (clientName: string): string => {
  // Use a service like UI Avatars to generate placeholder avatars
  const encodedName = encodeURIComponent(clientName || 'User')
  return `https://ui-avatars.com/api/?name=${encodedName}&background=random&color=fff&size=128&rounded=true`
}
